import { Model } from '@app/models/core/base.model';
import { DocumentType } from '@app/modules/documents-v2/enums/document-type.enum';
import { Translatable } from '@app/types/translatable.type';
import { CompanyDocument } from '@models/documents/company-document.model';
import { Drive } from '@models/documents/directory/drive.model';
import { EmployeeDocument } from '@models/documents/employee-document.model';
import { DroppableTableRowInterface } from '@table/interfaces/droppable-table-row.interface';
import { Folder } from './folder.model';

export class Node extends Model implements DroppableTableRowInterface {
    protected static _resource = 'directories/nodes';
    protected static _version = 'v2';

    protected static _type = 'directoryNodes';

    protected static _serializeAttributes = ['companyId'];

    get companyId(): number {
        return this._attributes['companyId'];
    }

    get name(): string {
        return this._attributes['name'];
    }

    get sourceType(): string {
        return this._attributes['sourceType'];
    }

    get sourceId(): string {
        return this._attributes['sourceId'];
    }

    get parentNodeId(): number | null {
        return this._attributes['parentNodeId'] ?? null;
    }

    get parent(): Node | null {
        return this.hasOne(Node, 'parent');
    }

    // TODO:: Replace 'any' return type with a specific type
    get source(): any {
        if (this.sourceType === 'CompanyDocument') {
            return this.hasOne(CompanyDocument, 'source');
        }

        if (this.sourceType === 'EmployeeDocument') {
            return this.hasOne(EmployeeDocument, 'source');
        }

        if (this.sourceType === 'DirectoryFolder') {
            return this.hasOne(Folder, 'source');
        }

        if (this.sourceType === 'DirectoryDrive') {
            return this.hasOne(Drive, 'source');
        }
    }

    get typeLabel(): string {
        if (this.isFolder()) {
            return 'document.folder';
        }

        if (this.isCompanyDocument()) {
            if (this.source.isStandardSigningDocument()) {
                return 'document.signingTemplate';
            }

            if (this.source.isOfferLetter()) {
                return 'document.offerLetterTemplate';
            }

            if (this.source.isViewOnly()) {
                return 'document.viewOnlyDocument';
            }

            return 'document.acknowledgementDocument';
        }

        if (this.isEmployeeDocument()) {
            if (this.source.isSignatureRequest()) {
                return 'document.signatureRequest';
            }

            if (this.source.isAcknowledgement()) {
                return 'document.acknowledgement';
            }

            // TODO  TrainingProgramRecordFile, Task

            return 'document.viewOnlyDocument';
        }

        return '';
    }

    get assignmentsCount(): number {
        return this._attributes['assignmentsCount'];
    }

    get assignmentsCompletedCount(): number {
        return this._attributes['assignmentsCompletedCount'];
    }

    get formattedAssignmentsText(): Translatable {
        if (!this.isCompanyDocument()) {
            return '-';
        }

        const companyDocument = this.source as CompanyDocument;

        if (companyDocument.type === DocumentType.standardSigningDocument) {
            return {
                key: 'document.signedByAssignment',
                params: {
                    assignmentsCompletedCount: this.assignmentsCompletedCount,
                    assignmentsCount: this.assignmentsCount,
                },
            };
        }

        if (companyDocument.type === DocumentType.acknowledgement) {
            return {
                key: 'document.acknowledgedByAssignment',
                params: {
                    assignmentsCompletedCount: this.assignmentsCompletedCount,
                    assignmentsCount: this.assignmentsCount,
                },
            };
        }

        if (companyDocument.type === DocumentType.offerLetter) {
            const key =
                this.assignmentsCount === 1 ? 'document.assignedToAssignmentSingular' : 'document.assignedToAssignment';

            return {
                key: key,
                params: {
                    assignmentsCount: this.assignmentsCount,
                },
            };
        }

        return '-';
    }

    isDrive(): boolean {
        return this.sourceType == 'DirectoryDrive';
    }

    isFolder(): boolean {
        return this.sourceType == 'DirectoryFolder';
    }

    isCompanyDocument(): boolean {
        return this.sourceType == 'CompanyDocument';
    }

    isEmployeeDocument(): boolean {
        return this.sourceType == 'EmployeeDocument';
    }

    isDocument(): boolean {
        return this.sourceType == 'CompanyDocument' || this.sourceType == 'EmployeeDocument';
    }

    rowCanReceiveDrop(): boolean {
        return this.isDrive() || this.isFolder();
    }
}
